<template>
  <!-- 首页 -->
  <div class="home">
    <!-- 首屏内容 -->
    <HomeContent @gotoLogin="gotoLogin"
      @logout="logout"
      @showFeedbackPopup="showFeedbackPopup"/>
    <div class="home-divider"></div>
    <div class="home-sections-bg">
      <HomeUGC />

      <!-- 交流板块 -->
      <CommunicationComponent @switch-com-tab="switchNavTab" @showKolPopup="showKolPopup"/>

      <!-- 抽奖板块 -->
      <div ref="lotterySection">
        <LotteryAndTask />
      </div>

      <!-- 开发进度前沿模块（视频） -->
      <div ref="developmentSection" class="development-section">
        <div class="development-title" :class="curLang"></div>
        <p class="development-subtitle">{{ langData.devSectionSubtitle }}</p>
        <div class="development-video-container">
          <div
            class="video-nav-btn prev-btn"
            @click="switchToPrevVideo"
          ></div>
          <div class="development-video-wrapper">
            <iframe
              v-if="currentVideoIndex === 0"
              src="https://www.youtube.com/embed/fi5obE1gE3c?start=159"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
            ></iframe>
            <iframe
              v-else-if="currentVideoIndex === 1"
              src="https://www.youtube.com/embed/CIqREwD1-gc?start=10"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
            ></iframe>
            <iframe
              v-else
              src="https://www.youtube.com/embed/npqNFgRxOPQ?start=41"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
            ></iframe>
          </div>
          <div
            class="video-nav-btn next-btn"
            @click="switchToNextVideo"
          ></div>
          <div class="bottom-decorator"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { inject, ref, onMounted, onUnmounted } from 'vue'
import CommunicationComponent from '@/components/communication.vue'
import LotteryAndTask from '@/components/LotteryAndTask.vue'
import LanguageHook from '@/hooks/language'
import HomeUGC from '@/components/HomeUGC.vue'
import HomeContent from '@/components/HomeContent.vue'

const { curLang, langData } = LanguageHook()

// Props & Emits
const emit = defineEmits(['showFeedbackPopup', 'showKolPopup', 'switchNavTab', 'gotoLogin', 'logout'])

// DOM 引用
const lotterySection = ref(null)
const developmentSection = ref(null)

// 视频切换相关
const currentVideoIndex = ref(0) // 0: pv-1, 1: pv

// 停留时间追踪
const sectionTimers = ref({
  lottery: {
    name: '积分抽奖',
    startTime: null,
    totalTime: 0,
    isVisible: false
  },
  development: {
    name: '开发进度前瞻',
    startTime: null,
    totalTime: 0,
    isVisible: false
  }
})

// 上报停留时间
const reportSectionStayTime = (sectionKey) => {
  const section = sectionTimers.value[sectionKey];

  // 如果当前正在查看，计算到当前时刻的时间
  if (section.isVisible && section.startTime) {
    section.totalTime += Date.now() - section.startTime;
    section.startTime = null;
  }

  // 只有总时长大于 1 秒才上报（避免误触）
  if (section.totalTime > 1000) {
    const duration = Math.floor(section.totalTime / 1000);
    const hours = Math.floor(duration / 3600);
    const minutes = Math.floor((duration % 3600) / 60);
    const seconds = duration % 60;
    const formattedDuration = `${hours > 0 ? hours + 'H' : ''}${minutes > 0 ? minutes + 'M' : ''}${seconds}S`;

    window.activityDataLayer.push({
      eventName: 'stay_time',
      eventType: section.name,
      eventValue: formattedDuration,
      aid: window.activity_name,
      gameId: window.game_id
    });

    // 上报后重置累计时间，避免重复上报
    section.totalTime = 0;
  }
};

// Intersection Observer 回调
const handleIntersection = (entries) => {
  entries.forEach(entry => {
    const target = entry.target;
    let sectionKey = null;

    if (target === lotterySection.value) {
      sectionKey = 'lottery';
    } else if (target === developmentSection.value) {
      sectionKey = 'development';
    }

    if (sectionKey) {
      const section = sectionTimers.value[sectionKey];

      if (entry.isIntersecting) {
        // 元素进入视口
        if (!section.isVisible) {
          section.isVisible = true;
          section.startTime = Date.now();
          window.activityDataLayer.push({
            eventName: 'view_page',
            eventType: section.name,
            aid: window.activity_name,
            gameId: window.game_id
          });
        }
      } else {
        // 元素离开视口 - 立即上报
        if (section.isVisible && section.startTime) {
          section.totalTime += Date.now() - section.startTime;
          section.startTime = null;
          section.isVisible = false;

          // 立即上报停留时间
          reportSectionStayTime(sectionKey);
        }
      }
    }
  });
};

// Intersection Observer 实例
let observer = null;

// 页面关闭/隐藏时上报所有区域的停留时间
const reportAllSections = () => {
  reportSectionStayTime('lottery');
  reportSectionStayTime('development');
};

// 页面卸载前上报（浏览器关闭、刷新等）
const handleBeforeUnload = () => {
  reportAllSections();
};

// 页面可见性变化时上报（切换标签页等）
const handleVisibilityChange = () => {
  if (document.hidden) {
    // 页面被隐藏时，上报当前正在查看的区域
    reportAllSections();
  }
};

// 方法代理
const showFeedbackPopup = () => emit('showFeedbackPopup')
const showKolPopup = () => emit('showKolPopup')
const switchNavTab = (tab) => emit('switchNavTab', tab)
const gotoLogin = () => emit('gotoLogin')
const logout = () => emit('logout')

// 视频切换方法
const switchToPrevVideo = () => {
  currentVideoIndex.value = (currentVideoIndex.value + 2) % 3
}

const switchToNextVideo = () => {
  currentVideoIndex.value = (currentVideoIndex.value + 1) % 3
}

// 生命周期
onMounted(() => {
  // 创建 Intersection Observer
  observer = new IntersectionObserver(handleIntersection, {
    root: null, // 使用视口作为根元素
    threshold: 0.3, // 当 30% 的元素可见时触发
    rootMargin: '0px'
  });

  // 观察两个区域
  if (lotterySection.value) {
    observer.observe(lotterySection.value);
  }
  if (developmentSection.value) {
    observer.observe(developmentSection.value);
  }

  // 监听页面卸载事件（浏览器关闭、刷新等）
  window.addEventListener('beforeunload', handleBeforeUnload);

  // 监听页面可见性变化（切换标签页等）
  document.addEventListener('visibilitychange', handleVisibilityChange);
});

onUnmounted(() => {
  // 上报所有区域的停留时间
  reportAllSections();

  // 清理 observer
  if (observer) {
    observer.disconnect();
    observer = null;
  }

  // 移除事件监听
  window.removeEventListener('beforeunload', handleBeforeUnload);
  document.removeEventListener('visibilitychange', handleVisibilityChange);
});
</script>
